import { Col, Container, Row } from 'react-bootstrap';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { addTenantLookup, getTenantLookupEntries, modifyTenantLookup, removeTenantLookup } from '../../services/api';

import Button from '@amzn/meridian/button';
import ConfirmationDialog from '../../../../global/confirmation-dialog/ConfirmationDialog';
import { DynamicTable } from '../../../../global/dynamic-table/DynamicTable';
import Icon from '@amzn/meridian/icon';
import { TenantIdContext } from '../../../../components/app/App';
import TenantLookupModal from './TenantLookupModal';
import _ from 'lodash';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import trashTokens from '@amzn/meridian-tokens/base/icon/trash';

const TenantLookup = () => {
  const tenantLookupColumns = useMemo(
    () => [
      {
        Header: 'Entry',
        accessor: 'tenantLookupEntry',
        width: 500,
      },
      {
        Header: 'Actions',
        accessor: 'manageEntry',
        disableResizing: true,
        disableSortBy: true,
      },
    ],
    []
  );

  const tenantId = useContext(TenantIdContext);
  const [isDataLoadingInProgress, setIsDataLoadingInProgress] = useState(true);
  const [showTableDataWhileLoading, setShowTableDataWhileLoading] = useState(false);
  const [tenantLookupEntries, setTenantLookupEntries] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState({});
  const [isEditModalOpen, setIsEditModalOpenOpen] = useState(false);
  const [isDeleteConfirmationDialogOpen, setIsDeleteConfirmationDialogOpen] = useState(false);
  const [deleteConfirmationCallback, setDeleteConfirmationCallback] = useState(() => {});

  const editLookup = entry => {
    setIsEditModalOpenOpen(true);
    setSelectedEntry(entry);
  };

  const addLookupEntry = updatedEntry => {
    let addTenantLookupRequest = {
      tenantId: tenantId,
      displayName: updatedEntry.displayName,
      lookupParams: updatedEntry.tenantLookupAttributes,
    };
    setShowTableDataWhileLoading(true);
    setIsDataLoadingInProgress(true);
    addTenantLookup(tenantId, addTenantLookupRequest).then(response => {
      setIsDataLoadingInProgress(false);
      if (!response?.data) {
        return;
      }
      tenantLookupEntries.push(updatedEntry);
      setTenantLookupEntries(_.cloneDeep(tenantLookupEntries));
    });
  };

  const modifyLookupEntry = updatedEntry => {
    let modifyTenantLookupRequest = {
      oldParams: selectedEntry,
      newParams: updatedEntry,
    };
    setShowTableDataWhileLoading(true);
    setIsDataLoadingInProgress(true);
    modifyTenantLookup(tenantId, modifyTenantLookupRequest).then(response => {
      setIsDataLoadingInProgress(false);
      if (!response?.data) {
        return;
      }
      for (let i = 0; i < tenantLookupEntries.length; i++) {
        if (_.isEqual(tenantLookupEntries[i], selectedEntry)) {
          tenantLookupEntries[i] = updatedEntry;
          break;
        }
      }
      setTenantLookupEntries(_.cloneDeep(tenantLookupEntries));
    });
  };

  const deleteLookupEntryRequestToBackend = entryToRemove => {
    let removeTenantLookupRequest = {
      lookupParams: entryToRemove,
    };
    setShowTableDataWhileLoading(true);
    setIsDataLoadingInProgress(true);
    removeTenantLookup(tenantId, removeTenantLookupRequest).then(response => {
      setIsDataLoadingInProgress(false);
      if (!response?.data) {
        return;
      }
      for (let i = 0; i < tenantLookupEntries.length; i++) {
        if (_.isEqual(tenantLookupEntries[i], entryToRemove)) {
          tenantLookupEntries.splice(i, 1);
          setTenantLookupEntries(_.cloneDeep(tenantLookupEntries));
          return;
        }
      }
    });
  };

  const deleteLookupEntry = entryToRemove => {
    // for explanation on why this is a double lambda, see
    // https://medium.com/swlh/how-to-store-a-function-with-the-usestate-hook-in-react-8a88dd4eede1
    setDeleteConfirmationCallback(() => () => deleteLookupEntryRequestToBackend(entryToRemove));
    setIsDeleteConfirmationDialogOpen(true);
  };

  const editModalConfirmHandler = updatedEntry => {
    if (_.isEmpty(updatedEntry) && !_.isEmpty(selectedEntry)) {
      deleteLookupEntry(selectedEntry);
    } else if (_.isEmpty(selectedEntry) && !_.isEmpty(updatedEntry)) {
      addLookupEntry(updatedEntry);
    } else if (!_.isEmpty(selectedEntry) && !_.isEmpty(updatedEntry)) {
      modifyLookupEntry(updatedEntry);
    }
  };

  useEffect(() => {
    if (tenantId) {
      setIsDataLoadingInProgress(true);
      setShowTableDataWhileLoading(false);
      getTenantLookupEntries(tenantId).then(response => {
        setTenantLookupEntries(response?.data?.tenantLookupList);
        setIsDataLoadingInProgress(false);
      });
    }
  }, [tenantId]);

  const tenantLookupData = [];
  if (tenantLookupEntries) {
    tenantLookupEntries.forEach((entry, index) => {
      tenantLookupData.push({
        tenantLookupEntry: entry.displayName,
        manageEntry: (
          <>
            <Button data-testid="EditTenantLookup" onClick={() => editLookup(entry)}>
              <Icon tokens={editTokens} />
            </Button>
            &nbsp;
            <Button data-testid="DeleteTenantLookup" onClick={() => deleteLookupEntry(entry)}>
              <Icon tokens={trashTokens} />
            </Button>
          </>
        ),
      });
    });
  }

  // FIXME: Not sure why, but a single fluid container overflows and displays a horizontal bar (both Chrome and Fox)
  return (
    <Container fluid>
      <Container fluid>
        <Row className="mt-3 mb-3">
          <Col>
            <h5>Tenant Configuration</h5>
          </Col>
          <Col className="justify-content-end">
            <Button className="float-right" data-testid="AddTenantLookup" onClick={() => editLookup({})}>
              Add new
            </Button>
          </Col>
        </Row>
        <Row>
          <DynamicTable
            columns={tenantLookupColumns}
            data={tenantLookupData}
            inProgress={isDataLoadingInProgress}
            showTableDataWhileInProgress={showTableDataWhileLoading}
          />
          <TenantLookupModal
            open={isEditModalOpen}
            setOpen={setIsEditModalOpenOpen}
            selectedEntry={selectedEntry}
            onConfirm={editModalConfirmHandler}
          />
          <ConfirmationDialog
            isOpen={isDeleteConfirmationDialogOpen}
            setIsOpen={setIsDeleteConfirmationDialogOpen}
            text="Are you sure you would like to remove this entry?"
            onConfirm={deleteConfirmationCallback}
          />
        </Row>
      </Container>
    </Container>
  );
};

export default TenantLookup;
